import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Header from "./Layout/Header";
import Footer from "./Layout/Footer";
import ErrorBoundary from "./ErrorBoundary";
import LoadingSpinner from "./Loader";
import LocationPicker from "./Location/LocationPicker";
import OffersHeader from "./Layout/OffersHeader";
import ProtectedRoute from "./Auth/ProtectedRoute";
import AdminDashboard from "./Admin/AdminDashboard";
import MobileFooter from "./Layout/MobileFooter";
import { useAuth } from "../context/AuthContext";
import helpIcon from "../assets/images/help.png";

const BrandSelector = lazy(() => import("./Brands/BrandSelector"));
const MenuList = lazy(() => import("./Menu/MenuList"));
const Checkout = lazy(() => import("./Checkout/Checkout"));
const OrderSuccess = lazy(() => import("./OrderSuccess"));
const MyOrders = lazy(() => import("./MyOrders"));
const Subscription = lazy(() => import("./Subscription/Subscription"));
const Profile = lazy(() => import("./Profile/Profile"));

const MainApp = ({ location, onLocationSet, onLoginClick }) => {
  const [isLocationModalOpen, setIsLocationModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (!location) {
      setIsLocationModalOpen(true);
    } else {
      setIsLocationModalOpen(false);
    }
  }, [location]);

  const handleLocationSet = (loc) => {
    onLocationSet(loc);
    setIsLocationModalOpen(false);
  };

  return (
    <Router>
      <MainAppContent
        location={location}
        onLocationSet={onLocationSet}
        onLoginClick={onLoginClick}
        isLocationModalOpen={isLocationModalOpen}
        setIsLocationModalOpen={setIsLocationModalOpen}
        handleLocationSet={handleLocationSet}
      />
    </Router>
  );
};

const MainAppContent = ({
  location,
  isLocationModalOpen,
  setIsLocationModalOpen,
  handleLocationSet,
  onLoginClick,
}) => {
  const { user, logout } = useAuth();
  const currentLocation = useLocation();

  // Scroll to top on route change
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentLocation.pathname]);

  const isDashboard = currentLocation.pathname.startsWith("/dashboard");
  const mainPage = currentLocation.pathname === "/";
  const menuPage =
    currentLocation.pathname.startsWith("/menu") ||
    currentLocation.pathname === "/";
  const subscriptionPage = currentLocation.pathname === "/subscription";
  const ordersPage = currentLocation.pathname === "/my-orders";
  const profilePage = currentLocation.pathname === "/profile";
  const orderSuccessPage =
    currentLocation.pathname.startsWith("/order-success");
  const [showHelpMenu, setShowHelpMenu] = React.useState(false);
  const helpMenuRef = React.useRef(null);

  React.useEffect(() => {
    if (!showHelpMenu) return;

    const handleClickOutside = (event) => {
      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target)) {
        setShowHelpMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showHelpMenu]);

  const showMobileFooter =
    mainPage ||
    subscriptionPage ||
    ordersPage ||
    profilePage ||
    orderSuccessPage;

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {!isDashboard && (
          <>
            <Header
              location={location}
              onOpenLocationModal={() => setIsLocationModalOpen(true)}
              menuPage={menuPage}
              showLocation={mainPage}
            />
            <div className="w-full bg-white md:hidden fixed top-0 left-0 z-50 shadow-sm px-2 py-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-700 w-[90%] min-w-0">
                  {!mainPage ? (
                    <button
                      onClick={() => window.history.back()}
                      className="flex items-center gap-2 font-medium text-gray-700"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                      Back
                    </button>
                  ) : (
                    <div
                      className="flex items-center gap-2 w-full"
                      onClick={() => setIsLocationModalOpen(true)}
                    >
                      <svg
                        className="w-5 h-5 text-green-600 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <div className="flex items-center gap-1 overflow-hidden flex-1">
                        <span
                          className="truncate font-medium flex-1"
                          title={location?.address || "Select Location"}
                        >
                          {location?.address || "Select Location"}
                        </span>
                        <svg
                          className="w-4 h-4 text-gray-400 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  ref={helpMenuRef}
                  className="relative flex-shrink-0 bg-white pl-2 z-10"
                >
                  <button
                    className="flex flex-col items-center text-gray-500 hover:text-green-600 transition-colors"
                    onClick={() => setShowHelpMenu(!showHelpMenu)}
                  >
                    <img
                      src={helpIcon}
                      style={{
                        width: "24px",
                        height: "24px",
                        objectFit: "contain",
                      }}
                      alt="help support"
                    />
                    <span style={{ color: "#0B9444", fontSize: "10px" }}>
                      Help
                    </span>
                  </button>

                  {showHelpMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <a
                        href="https://wa.me/917709732049"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowHelpMenu(false)}
                      >
                        <svg
                          className="w-5 h-5 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">
                          Contact Us
                        </span>
                      </a>
                      <a
                        href="tel:+917709732049"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowHelpMenu(false)}
                      >
                        <svg
                          className="w-5 h-5 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">
                          Call Us
                        </span>
                      </a>
                      <a
                        href="mailto:gutfulgoodness@gmail.com"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowHelpMenu(false)}
                      >
                        <svg
                          className="w-5 h-5 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-sm font-medium text-gray-700">
                          Email Us
                        </span>
                      </a>

                      {/* Login / Logout Option */}
                      {user ? (
                        <button
                          onClick={() => {
                            logout();
                            setShowHelpMenu(false);
                            window.location.reload();
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors"
                        >
                          <svg
                            className="w-5 h-5 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          <span className="text-sm font-medium text-red-600">
                            Logout
                          </span>
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setShowHelpMenu(false);
                            onLoginClick();
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-green-50 transition-colors"
                        >
                          <svg
                            className="w-5 h-5 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          <span className="text-sm font-medium text-green-600">
                            Login
                          </span>
                        </button>
                      )}
                      <div className="px-4 py-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Timing</p>
                        <p className="text-sm font-medium text-gray-700">
                          1 PM - 11 PM
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="sticky top-[5px] md:static z-40">
              <OffersHeader className="md:mt-0 mt-10 md:mt-0" />
            </div>
          </>
        )}

        <main className="flex-1">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<BrandSelector />} />
              <Route path="/menu/:brandId" element={<MenuList />} />
              <Route
                path="/checkout"
                element={<Checkout location={location} />}
              />
              <Route
                path="/order-success/:orderId"
                element={<OrderSuccess />}
              />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/profile" element={<Profile />} />
            </Routes>
          </Suspense>
        </main>

        {!isDashboard && <Footer />}
        {!isDashboard && showMobileFooter && <MobileFooter />}
      </div>

      {/* Location Picker Modal */}
      {isLocationModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden relative">
            {/* {location && ( */}
            <button
              onClick={() => setIsLocationModalOpen(false)}
              className="absolute top-1 right-2 z-10 p-2 hover:bg-gray-100 rounded-full transition"
            >
              <svg
                className="w-6 h-6 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            {/* )} */}
            <div className="max-h-[90vh] overflow-y-auto">
              <LocationPicker
                onLocationSet={handleLocationSet}
                isModal={true}
              />
            </div>
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
};

export default MainApp;
